import {
  Children,
  isValidElement,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Select as AntSelect } from 'antd';

// Shadcn-style decomposed Select API on top of antd Select. The wrapper
// collects <SelectItem> children inside <SelectContent> and forwards them
// as antd `options`. Trigger/Value contribute placeholder and a11y props.

interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  children: ReactNode;
}

interface SelectTriggerProps {
  id?: string;
  invalid?: boolean;
  className?: string;
  children: ReactNode;
  'aria-describedby'?: string;
}

interface SelectValueProps {
  placeholder?: string;
  children?: ReactNode;
}

interface SelectContentProps {
  children: ReactNode;
  position?: 'item-aligned' | 'popper';
}

interface SelectItemProps {
  value: string;
  children: ReactNode;
  disabled?: boolean;
}

const TRIGGER_MARK = Symbol.for('aidu.select.trigger');
const VALUE_MARK = Symbol.for('aidu.select.value');
const CONTENT_MARK = Symbol.for('aidu.select.content');
const ITEM_MARK = Symbol.for('aidu.select.item');

type Marked<P> = ((p: P) => ReactNode) & { __mark?: symbol };

const TriggerImpl: Marked<SelectTriggerProps> = (() => null) as never;
TriggerImpl.__mark = TRIGGER_MARK;
export const SelectTrigger = TriggerImpl as unknown as (p: SelectTriggerProps) => ReactNode;

const ValueImpl: Marked<SelectValueProps> = (() => null) as never;
ValueImpl.__mark = VALUE_MARK;
export const SelectValue = ValueImpl as unknown as (p: SelectValueProps) => ReactNode;

const ContentImpl: Marked<SelectContentProps> = (() => null) as never;
ContentImpl.__mark = CONTENT_MARK;
export const SelectContent = ContentImpl as unknown as (p: SelectContentProps) => ReactNode;

const ItemImpl: Marked<SelectItemProps> = (() => null) as never;
ItemImpl.__mark = ITEM_MARK;
export const SelectItem = ItemImpl as unknown as (p: SelectItemProps) => ReactNode;

// Convenience aliases shipped from the old API.
export const SelectGroup = ({ children }: { children: ReactNode }) => <>{children}</>;

function flatten(children: ReactNode): ReactElement[] {
  const out: ReactElement[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    out.push(child);
    const c = (child.props as { children?: ReactNode }).children;
    if (c !== undefined) out.push(...flatten(c));
  });
  return out;
}

function isMarked(element: ReactElement, mark: symbol): boolean {
  const t = element.type as Marked<unknown> | undefined;
  return Boolean(t && t.__mark === mark);
}

export function Select({
  value,
  defaultValue,
  onValueChange,
  disabled,
  children,
}: SelectProps) {
  const all = useMemo(() => flatten(children), [children]);

  const trigger = all.find((el) => isMarked(el, TRIGGER_MARK));
  const triggerProps = (trigger?.props ?? {}) as SelectTriggerProps;

  const valueEl = all.find((el) => isMarked(el, VALUE_MARK));
  const placeholder = (valueEl?.props as SelectValueProps | undefined)?.placeholder;

  const options = useMemo(
    () =>
      all
        .filter((el) => isMarked(el, ITEM_MARK))
        .map((el) => {
          const p = el.props as SelectItemProps;
          return { value: p.value, label: p.children, disabled: p.disabled };
        }),
    [all],
  );

  return (
    <AntSelect
      id={triggerProps.id}
      status={triggerProps.invalid ? 'error' : undefined}
      className={triggerProps.className}
      value={value}
      defaultValue={defaultValue}
      onChange={(v) => onValueChange?.(v as string)}
      placeholder={placeholder}
      disabled={disabled}
      options={options}
      aria-describedby={triggerProps['aria-describedby']}
      style={{ width: '100%' }}
    />
  );
}
